// packages/shared/api-client/endpoints/users.js
// User and member directory API endpoints

import { baseApi } from "../baseApi";

/**
 * User API endpoints
 * Provides user profile, directory, and user management
 */
const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get current user profile
    getCurrentUser: builder.query({
      query: () => "/api/users/me",
      providesTags: ["User"],
    }),

    // Update current user profile
    updateCurrentUser: builder.mutation({
      query: (userData) => ({
        url: "/api/users/me",
        method: "PUT",
        body: userData,
      }),
      invalidatesTags: ["User"],
    }),

    // Complete user onboarding
    completeOnboarding: builder.mutation({
      query: (data = {}) => ({
        url: "/api/users/me/onboarding/complete",
        method: "POST",
        body: data, // { notificationPreferences? }
      }),
      invalidatesTags: ["User"],
    }),

    // Get user directory (member list)
    getUsers: builder.query({
      query: (params = {}) => ({
        url: "/api/users",
        params, // { search, department, role, page, limit }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "User", id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),

    // Get specific user by ID
    getUserById: builder.query({
      query: (id) => `/api/users/${id}`,
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    // Register FCM token for push notifications
    registerFcmToken: builder.mutation({
      query: ({ userId, fcmToken }) => ({
        url: `/api/users/${userId}/fcm-token`,
        method: "POST",
        body: { fcmToken },
      }),
    }),

    // Admin: Create new user
    createUser: builder.mutation({
      query: (userData) => ({
        url: "/api/users",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    // Admin: Update user
    updateUser: builder.mutation({
      query: ({ id, ...userData }) => ({
        url: `/api/users/${id}`,
        method: "PUT",
        body: userData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Delete user
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/api/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Activate user
    activateUser: builder.mutation({
      query: (id) => ({
        url: `/api/users/${id}/activate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Deactivate user
    deactivateUser: builder.mutation({
      query: (id) => ({
        url: `/api/users/${id}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    toggleGoodStanding: builder.mutation({
      query: ({ id, goodStanding }) => ({
        url: `/api/users/${id}/good-standing`,
        method: "PATCH",
        body: { goodStanding },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Assign role to user
    assignRole: builder.mutation({
      query: ({ userId, roleId, expiresAt }) => ({
        url: `/api/users/${userId}/roles`,
        method: "POST",
        body: { roleId, expiresAt },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Remove role from user
    removeRole: builder.mutation({
      query: ({ userId, roleId }) => ({
        url: `/api/users/${userId}/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
      ],
    }),

    // User approval management
    getPendingUsers: builder.query({
      query: ({ page, limit, departmentId } = {}) => ({
        url: "/api/users/pending",
        method: "GET",
        params: { page, limit, departmentId },
      }),
      providesTags: ["PendingUser"],
    }),

    approveUser: builder.mutation({
      query: ({ userId, roleIds }) => ({
        url: `/api/users/${userId}/approve`,
        method: "POST",
        body: { roleIds },
      }),
      invalidatesTags: ["PendingUser", "User"],
    }),

    rejectUser: builder.mutation({
      query: ({ userId, rejectionReason }) => ({
        url: `/api/users/${userId}/reject`,
        method: "POST",
        body: { rejectionReason },
      }),
      invalidatesTags: (result, error, { userId }) => [
        "PendingUser",
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
      ],
    }),

    // Self-service account deletion request (requires password confirmation)
    requestAccountDeletion: builder.mutation({
      query: ({ password }) => ({
        url: "/api/users/me",
        method: "DELETE",
        body: { password },
      }),
    }),

    // Admin: Cancel a pending deletion request
    cancelDeletionRequest: builder.mutation({
      query: (id) => ({
        url: `/api/users/${id}/cancel-deletion`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Mark user as retired
    markUserRetired: builder.mutation({
      query: ({ id, retiredAt, setInactive }) => ({
        url: `/api/users/${id}/mark-retired`,
        method: "POST",
        body: { retiredAt, setInactive },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Mark user as deceased
    markUserDeceased: builder.mutation({
      query: ({ id, deceasedAt }) => ({
        url: `/api/users/${id}/mark-deceased`,
        method: "POST",
        body: { deceasedAt },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Admin: Undo retirement (restore to active)
    unretireUser: builder.mutation({
      query: (id) => ({
        url: `/api/users/${id}/unretire`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const useGetCurrentUserQuery = userApi.useGetCurrentUserQuery;
export const useUpdateCurrentUserMutation =
  userApi.useUpdateCurrentUserMutation;
export const useCompleteOnboardingMutation =
  userApi.useCompleteOnboardingMutation;
export const useGetUsersQuery = userApi.useGetUsersQuery;
export const useLazyGetUsersQuery = userApi.useLazyGetUsersQuery;
export const useGetUserByIdQuery = userApi.useGetUserByIdQuery;
export const useRegisterFcmTokenMutation = userApi.useRegisterFcmTokenMutation;
export const useCreateUserMutation = userApi.useCreateUserMutation;
export const useUpdateUserMutation = userApi.useUpdateUserMutation;
export const useDeleteUserMutation = userApi.useDeleteUserMutation;
export const useActivateUserMutation = userApi.useActivateUserMutation;
export const useDeactivateUserMutation = userApi.useDeactivateUserMutation;
export const useAssignRoleMutation = userApi.useAssignRoleMutation;
export const useRemoveRoleMutation = userApi.useRemoveRoleMutation;
export const useGetPendingUsersQuery = userApi.useGetPendingUsersQuery;
export const useApproveUserMutation = userApi.useApproveUserMutation;
export const useRejectUserMutation = userApi.useRejectUserMutation;
export const useRequestAccountDeletionMutation =
  userApi.useRequestAccountDeletionMutation;
export const useCancelDeletionRequestMutation =
  userApi.useCancelDeletionRequestMutation;
export const useMarkUserRetiredMutation = userApi.useMarkUserRetiredMutation;
export const useMarkUserDeceasedMutation = userApi.useMarkUserDeceasedMutation;
export const useUnretireUserMutation = userApi.useUnretireUserMutation;
export const useToggleGoodStandingMutation = userApi.useToggleGoodStandingMutation;
